/**
 * Location suggestion (sections/geo-suggest.liquid)
 *
 * <geo-suggest data-check-country data-check-language data-delay data-remember-days ...>
 *   1. Country: asks Shopify's own /browsing_context_suggestions.json for the visitor's IP country
 *      (cached for the session). Only countries the shop sells to are suggested.
 *   2. Language: walks navigator.languages and picks the first one published for that
 *      country's market ("de-AT" matches "de", "zh-Hant" matches "zh-TW").
 *   3. If the suggested country or language differs from the current store, it opens the dialog,
 *      in the suggested language when that language is available in the current market.
 *   4. "Switch" submits the theme's localization form (Shopify redirects to the right market,
 *      currency and language). "Stay", the close button and Escape keep the current store.
 *      Both are remembered for data-remember-days, per suggestion.
 *
 * Skips bots and headless browsers. In the theme editor it never opens on its own; selecting the
 * section shows a preview. Native browser APIs only; safe to load twice.
 */
if (!customElements.get('geo-suggest')) {
  const BOT_PATTERN = /bot|crawl|spider|slurp|lighthouse|headless|prerender|pagespeed|gtmetrix/i;
  const DECISION_KEY = 'geo-suggest:decision';
  const IP_KEY = 'geo-suggest:ip-country';
  const DAY = 86400000;
  const SCRIPT_ALIASES = { 'zh-hant': 'zh-tw', 'zh-hk': 'zh-tw', 'zh-mo': 'zh-tw', 'zh-hans': 'zh-cn', 'zh-sg': 'zh-cn' };

  const storage = {
    get(store, key) {
      try {
        return window[store].getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(store, key, value) {
      try {
        window[store].setItem(key, value);
      } catch (error) {
        /* storage blocked: the popup may ask again, nothing breaks */
      }
    },
  };

  const sameLanguage = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
  const fill = (template, values) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  /**
   * First browser language available in `available` (exact, then script alias, then primary subtag).
   * @param {readonly string[]} browserLanguages
   * @param {string[]} available
   */
  function matchLanguage(browserLanguages, available) {
    const lowered = available.map((code) => code.toLowerCase());
    for (const raw of browserLanguages) {
      const tag = String(raw).toLowerCase();
      const aliased = SCRIPT_ALIASES[tag] || SCRIPT_ALIASES[tag.split('-').slice(0, 2).join('-')] || tag;
      let index = lowered.indexOf(aliased);
      if (index === -1 && !aliased.startsWith('zh')) {
        const primary = aliased.split('-')[0];
        index = lowered.findIndex((code) => code.split('-')[0] === primary);
      }
      if (index !== -1) return available[index];
    }
    return null;
  }

  class GeoSuggest extends HTMLElement {
    #controller = new AbortController();
    #timer = null;
    #suggestion = null;

    connectedCallback() {
      const source = this.querySelector('[data-geo-data]');
      if (!source) return;
      this.data = this.#parse(source.textContent);
      if (!this.data) return;

      this.dialog = this.querySelector('[data-geo-dialog]');
      this.form = this.querySelector('form');
      this.countryInput = this.querySelector('[data-geo-country-input]');
      this.languageInput = this.querySelector('[data-geo-language-input]');
      this.countrySelect = this.querySelector('[data-geo-country-select]');
      this.languageSelect = this.querySelector('[data-geo-language-select]');
      this.checkCountry = this.dataset.checkCountry === 'true';
      this.checkLanguage = this.dataset.checkLanguage === 'true';
      this.rememberDays = Number(this.dataset.rememberDays) || 30;

      const { signal } = this.#controller;
      this.querySelectorAll('[data-geo-stay]').forEach((button) =>
        button.addEventListener('click', () => this.#stay(), { signal })
      );
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.#stay();
      }, { signal });
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('geo-suggest-open'), { signal });
      this.form?.addEventListener('submit', () => this.#remember('switch'), { signal });
      this.countrySelect?.addEventListener('change', () => this.#onCountryChange(), { signal });
      this.languageSelect?.addEventListener('change', () => this.#onLanguageChange(), { signal });

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.#preview();
        }, { signal });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.dialog.close();
        }, { signal });
        return;
      }

      if (BOT_PATTERN.test(navigator.userAgent) || navigator.webdriver) return;
      this.#run();
    }

    disconnectedCallback() {
      window.clearTimeout(this.#timer);
      this.#controller.abort();
      this.#controller = new AbortController();
    }

    #parse(text) {
      try {
        return JSON.parse(text);
      } catch (error) {
        return null;
      }
    }

    get #countries() {
      return new Map(this.data.countries.map((entry) => [entry[0], entry]));
    }

    /* ---------- Detection ---------- */

    async #run() {
      const ipCountry = this.checkCountry ? await this.#detectCountry() : null;
      const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language || ''];
      const suggestion = this.#suggest(ipCountry, browserLanguages);
      if (!suggestion || this.#recentlyDecided(suggestion)) return;

      const view = await this.#viewFor(suggestion.language);
      const delay = Math.max(0, Number(this.dataset.delay) || 0);
      this.#timer = window.setTimeout(() => this.#open(suggestion, view), delay);
    }

    async #detectCountry() {
      const cached = storage.get('sessionStorage', IP_KEY);
      if (cached !== null) return cached || null;
      try {
        const response = await fetch('/browsing_context_suggestions.json?country[enabled]=true', {
          credentials: 'same-origin',
          signal: this.#controller.signal,
        });
        const json = await response.json();
        const code = json?.detected_values?.country?.handle || '';
        storage.set('sessionStorage', IP_KEY, code);
        return code || null;
      } catch (error) {
        return null;
      }
    }

    /**
     * @param {string|null} ipCountry
     * @param {readonly string[]} browserLanguages
     */
    #suggest(ipCountry, browserLanguages) {
      const { current } = this.data;
      const countries = this.#countries;

      let country = current.country;
      if (this.checkCountry && ipCountry && countries.has(ipCountry)) country = ipCountry;

      const marketLanguages = countries.get(country)?.[4]?.length ? countries.get(country)[4] : [current.language];
      let language = this.checkLanguage ? matchLanguage(browserLanguages, marketLanguages) : null;
      if (!language) {
        language = marketLanguages.find((code) => sameLanguage(code, current.language)) || marketLanguages[0];
      }

      const countryChanged = country !== current.country;
      const languageChanged = !sameLanguage(language, current.language);
      if (!countryChanged && !languageChanged) return null;
      return { country, language, countryChanged, languageChanged };
    }

    #recentlyDecided(suggestion) {
      const stored = this.#parse(storage.get('localStorage', DECISION_KEY) || 'null');
      if (!stored) return false;
      const sameSuggestion = stored.country === suggestion.country && sameLanguage(stored.language, suggestion.language);
      return sameSuggestion && Date.now() - stored.at < this.rememberDays * DAY;
    }

    #remember(choice) {
      if (!this.#suggestion) return;
      storage.set(
        'localStorage',
        DECISION_KEY,
        JSON.stringify({ choice, country: this.#suggestion.country, language: this.#suggestion.language, at: Date.now() })
      );
    }

    /** This section rendered in the suggested language, when that language exists in the current market. */
    async #viewFor(language) {
      const root = Object.entries(this.data.roots || {}).find(([code]) => sameLanguage(code, language))?.[1];
      if (!root || sameLanguage(language, this.data.current.language)) return this.data;
      try {
        const url = `${root.replace(/\/$/, '')}/?section_id=${encodeURIComponent(this.dataset.sectionId)}`;
        const html = await (await fetch(url, { signal: this.#controller.signal })).text();
        const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-geo-data]');
        return (node && this.#parse(node.textContent)) || this.data;
      } catch (error) {
        return this.data;
      }
    }

    /* ---------- Dialog ---------- */

    #open(suggestion, view) {
      this.#suggestion = suggestion;
      this.view = view;
      this.dialog.setAttribute('lang', suggestion.language);
      this.dialog.setAttribute('dir', view.direction === 'rtl' ? 'rtl' : 'ltr');
      this.#render();
      this.#buildSelects();
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('geo-suggest-open');
    }

    #render() {
      const { strings } = this.view;
      const suggestion = this.#suggestion;
      const target = this.#countryInfo(suggestion.country);
      const current = this.#countryInfo(this.data.current.country);
      const languageName = this.#languageName(suggestion.language);
      const values = {
        country: target.name,
        currency: `${target.currency} ${target.symbol}`.trim(),
        language: languageName,
        current: suggestion.countryChanged ? current.name : this.#languageName(this.data.current.language),
      };

      const heading = suggestion.countryChanged ? strings.heading_country : strings.heading_language;
      let body = strings.body_language;
      if (suggestion.countryChanged && suggestion.languageChanged) body = strings.body_both;
      else if (suggestion.countryChanged) body = strings.body_country;

      this.querySelector('[data-geo-heading]').textContent = fill(heading, values);
      this.querySelector('[data-geo-body]').textContent = fill(body, values);
      this.querySelector('[data-geo-switch]').textContent = fill(
        suggestion.countryChanged ? strings.switch_country : strings.switch_language,
        values
      );
      this.querySelector('[data-geo-stay-label]').textContent = fill(strings.stay, values);
      this.querySelector('[data-geo-close]')?.setAttribute('aria-label', strings.close);
      const changeLabel = this.querySelector('[data-geo-change-label]');
      if (changeLabel) changeLabel.textContent = strings.change;
      const countryLabel = this.querySelector('[data-geo-country-label]');
      if (countryLabel) countryLabel.textContent = strings.country_label;
      const languageLabel = this.querySelector('[data-geo-language-label]');
      if (languageLabel) languageLabel.textContent = strings.language_label;
      this.dialog.setAttribute('aria-label', strings.dialog_label);

      this.querySelector('[data-geo-target-country]').textContent = target.name;
      this.querySelector('[data-geo-target-meta]').textContent = `${values.currency} · ${languageName}`;
      const flag = this.querySelector('[data-geo-flag]');
      if (flag) flag.src = this.#flagUrl(suggestion.country);

      this.countryInput.value = suggestion.country;
      this.languageInput.value = suggestion.language;
    }

    #countryInfo(code) {
      const entry =
        new Map((this.view || this.data).countries.map((item) => [item[0], item])).get(code) || this.#countries.get(code);
      return entry
        ? { code, name: entry[1], currency: entry[2], symbol: entry[3], languages: entry[4] }
        : { code, name: code, currency: '', symbol: '', languages: [] };
    }

    #languageName(code) {
      const names = { ...this.data.languages, ...(this.view?.languages || {}) };
      const key = Object.keys(names).find((name) => sameLanguage(name, code));
      return key ? names[key] : code;
    }

    #flagUrl(code) {
      const sample = this.dataset.flagSample || '';
      const own = this.dataset.flagCode || '';
      return own ? sample.replace(`/flags/${own}.svg`, `/flags/${String(code).toLowerCase()}.svg`) : sample;
    }

    /* ---------- Change country / language ---------- */

    #buildSelects() {
      if (!this.countrySelect || !this.languageSelect) return;
      const list = [...(this.view || this.data).countries].sort(
        (a, b) => b[5] - a[5] || String(a[1]).localeCompare(String(b[1]), this.#suggestion.language)
      );
      this.countrySelect.replaceChildren(
        ...list.map((entry) => new Option(`${entry[1]} (${entry[2]} ${entry[3]})`.trim(), entry[0], false, entry[0] === this.#suggestion.country))
      );
      this.#fillLanguages(this.#suggestion.language);
    }

    #fillLanguages(preferred) {
      const languages = this.#countryInfo(this.countrySelect.value).languages;
      const options = languages.length ? languages : [this.data.current.language];
      const chosen = options.find((code) => sameLanguage(code, preferred)) || options[0];
      this.languageSelect.replaceChildren(
        ...options.map((code) => new Option(this.#languageName(code), code, false, sameLanguage(code, chosen)))
      );
      this.languageSelect.disabled = options.length < 2;
    }

    #onCountryChange() {
      this.#fillLanguages(this.languageSelect.value);
      this.#applySelection();
    }

    #onLanguageChange() {
      this.#applySelection();
    }

    #applySelection() {
      const country = this.countrySelect.value;
      const language = this.languageSelect.value;
      this.#suggestion = {
        country,
        language,
        countryChanged: country !== this.data.current.country,
        languageChanged: !sameLanguage(language, this.data.current.language),
      };
      if (!this.#suggestion.countryChanged && !this.#suggestion.languageChanged) {
        this.#suggestion.countryChanged = true; // keeps a sensible button label when the shopper picks the current store
      }
      this.#render();
    }

    #stay() {
      this.#remember('stay');
      this.dialog.close();
    }

    /* ---------- Theme editor preview ---------- */

    #preview() {
      const { current } = this.data;
      const other = [...this.data.countries].sort((a, b) => b[5] - a[5]).find((entry) => entry[0] !== current.country);
      if (!other) {
        this.data.countries.push(['DE', 'Germany', 'EUR', '€', ['de'], 1]);
        this.data.languages.de = this.data.languages.de || 'Deutsch';
      }
      const country = other ? other[0] : 'DE';
      const languages = this.#countries.get(country)[4];
      const language = languages.find((code) => !sameLanguage(code, current.language)) || languages[0] || current.language;
      this.#open(
        { country, language, countryChanged: true, languageChanged: !sameLanguage(language, current.language) },
        this.data
      );
    }
  }

  customElements.define('geo-suggest', GeoSuggest);
}
